import { Dispatch, RefObject, SetStateAction, useEffect, useRef, useState } from "react";
import { ChartWrapperOptions, GoogleVizEventName, ReactGoogleChartEvent } from "react-google-charts";
import { useDeepCompareEffectNoCheck } from "use-deep-compare-effect";

import { PortfolioDatesData } from "../../../models";
import { useCustomEventListener } from "../../../utils";
import { ChartReferenceNodes, PortfolioRoadmapRowLabelData, ViewMode } from "./models";
import {
  addMarkerAndSetScrollPosition,
  adjustTimeline,
  calculateViewMode,
  getFormatFromViewMode,
  getPortfolioRoadmapRowLabelData,
  getPortfolioRoadmapTimelineData,
  getTopLevelRoadmapSvg,
  mutateBars,
  onScroll,
  resetChartHeight,
  updateTooltipPosition,
} from "./utils";

interface UsePortfolioRoadmapReturnData {
  roadmapRef: RefObject<HTMLDivElement>;
  rowLabelsRef: RefObject<HTMLDivElement>;

  rowLabelData: PortfolioRoadmapRowLabelData;
  chartData: unknown[];

  options: ChartWrapperOptions["options"];
  chartEvents: ReactGoogleChartEvent[];
  height?: number;
  viewMode: ViewMode;
  showMilestones: boolean;
  width: string;
  onChangeViewMode: (viewMode: ViewMode) => void;
  onToggleMilestones: (show: boolean) => void;
  onScroll: () => void;
}

interface UsePortfolioRoadmapProps {
  data: PortfolioDatesData[];
  setIsLoading: Dispatch<SetStateAction<boolean>>;
}

export const usePortfolioRoadmap = ({
  data,
  setIsLoading,
}: UsePortfolioRoadmapProps): UsePortfolioRoadmapReturnData => {
  const roadmapRef = useRef<HTMLDivElement | null>(null);
  const rowLabelsRef = useRef<HTMLDivElement | null>(null);
  const clearSelectionRef = useRef<{ clear: (() => void) | null }>({ clear: null });

  const [viewMode, setViewMode] = useState<ViewMode>(ViewMode.Years1);
  const [showMilestones, setShowMilestones] = useState(true);

  const [chartData, setChartData] = useState(getPortfolioRoadmapTimelineData(data, showMilestones));
  const [rowLabelData, setRowLabelData] = useState(getPortfolioRoadmapRowLabelData(data));

  const [height, setHeight] = useState<number>();
  const [shouldResetChartHeight, setShouldResetChartHeight] = useState(true);
  const [minTimelineWidth, setMinTimelineWidth] = useState(0);

  useEffect(() => {
    const containerEl = roadmapRef.current;
    if (!containerEl) return undefined;

    const updateMinTimelineWidth = (): void => {
      const nextMinTimelineWidth = Math.max(containerEl.clientWidth - (rowLabelsRef.current?.offsetWidth ?? 0), 0);
      setMinTimelineWidth((currentWidth) =>
        currentWidth === nextMinTimelineWidth ? currentWidth : nextMinTimelineWidth
      );
    };

    updateMinTimelineWidth();

    const observer = new ResizeObserver(() => {
      updateMinTimelineWidth();
    });

    observer.observe(containerEl);
    if (rowLabelsRef.current) observer.observe(rowLabelsRef.current);

    return () => {
      if (rowLabelsRef.current) observer.unobserve(rowLabelsRef.current);
      observer.unobserve(containerEl);
      observer.disconnect();
    };
  }, [rowLabelData]);

  const onChangeViewMode = (newViewMode: ViewMode): void => {
    setIsLoading(true);
    setViewMode(newViewMode);
  };

  const onToggleMilestones = (show: boolean): void => {
    setIsLoading(true);
    setShowMilestones(show);
  };

  const chartReferenceNodes: ChartReferenceNodes = {};

  const options: ChartWrapperOptions["options"] = {
    tooltip: { isHtml: true },
    timeline: {
      showRowLabels: false,
      showBarLabels: false,
      barLabelStyle: { fontSize: 17.622 },
    },
    hAxis: {
      textStyle: {
        fontSize: 16,
      },
      format: getFormatFromViewMode(viewMode),
    },
    allowHtml: true,
    alternatingRowStyle: false,
    avoidOverlappingGridLines: false,
  };

  const chartEvents: ReactGoogleChartEvent[] = [
    {
      eventName: "ready",
      callback: ({ chartWrapper, google }) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const chart = chartWrapper?.getChart() as any;
        google.visualization.events.addListener(chart, "onmouseover" as GoogleVizEventName, () => {
          mutateBars(chartReferenceNodes);
        });
        google.visualization.events.addListener(chart, "onmouseout" as GoogleVizEventName, () => {
          mutateBars(chartReferenceNodes);
        });
        google.visualization.events.addListener(chart, "select", () => {
          const selection = chart.getSelection();
          if (selection[0].row !== null) {
            const tooltip = roadmapRef.current?.querySelector(
              ".google-visualization-tooltip:not([clone])"
            ) as HTMLElement | null;

            if (chart.tooltipClone && chart.tooltipClone.parentNode) {
              chart.tooltipClone.parentNode.removeChild(chart.tooltipClone);
            }

            if (tooltip) {
              tooltip.style.height = height && height < 198 ? `${height}px` : "auto";
            }

            chart.tooltipClone = tooltip?.cloneNode(true);
            chartReferenceNodes.tooltipClone = chart.tooltipClone;

            chart.tooltipClone.setAttribute("clone", true);

            updateTooltipPosition(roadmapRef, chartReferenceNodes);

            chart.tooltipClone.style.pointerEvents = "auto";

            tooltip?.parentNode?.insertBefore(chart.tooltipClone, chart.tooltip);

            mutateBars(chartReferenceNodes);
          }
        });

        const svgNode = getTopLevelRoadmapSvg(roadmapRef);

        resetChartHeight(rowLabelsRef, height, setHeight, shouldResetChartHeight, setShouldResetChartHeight, svgNode);
        addMarkerAndSetScrollPosition(roadmapRef, viewMode, svgNode, height, data, showMilestones);
        adjustTimeline(roadmapRef, chartReferenceNodes, svgNode);
        mutateBars(chartReferenceNodes, svgNode);
        onScroll(roadmapRef, chartReferenceNodes, height);
        setIsLoading(false);

        clearSelectionRef.current.clear = (): void => {
          if (chart.tooltipClone && chart.tooltipClone.parentNode) {
            chart.tooltipClone.parentNode.removeChild(chart.tooltipClone);
          }
          chart.setSelection([]);
          mutateBars(chartReferenceNodes);
        };
      },
    },
  ];

  useCustomEventListener("closeTooltip", () => {
    if (clearSelectionRef.current.clear) clearSelectionRef.current.clear();
  });

  useDeepCompareEffectNoCheck(() => {
    setShouldResetChartHeight(true);
    setChartData(getPortfolioRoadmapTimelineData(data, showMilestones));
    setRowLabelData(getPortfolioRoadmapRowLabelData(data));
  }, [data, showMilestones]);

  return {
    chartData,
    rowLabelData,
    roadmapRef,
    rowLabelsRef,
    options,
    chartEvents,
    height,
    viewMode,
    showMilestones,
    width: calculateViewMode(viewMode, data, showMilestones, minTimelineWidth),
    onChangeViewMode,
    onToggleMilestones,
    onScroll: () => onScroll(roadmapRef, chartReferenceNodes, height),
  };
};
